/**
 * Auth provider — Keycloak OIDC, Authorization Code + PKCE.
 *
 * PKCE and not the implicit flow, and no client secret: this is a public
 * client running in a browser, where a secret would be readable by anyone who
 * opened devtools. PKCE is what makes a public client safe — the authorization
 * code is useless without the verifier, which never leaves this tab.
 *
 * The Keycloak token is never sent to PostgREST. It proves identity; the
 * portal exchanges it for a short-lived token naming the caller's Postgres
 * role (see token.ts). Two tokens, two jobs.
 *
 * TOKENS live in sessionStorage: scoped to the tab, gone when it closes, and
 * not shared with another tab that may belong to a different person on a
 * shared machine.
 *
 * THE PKCE VERIFIER DOES NOT. It is written before leaving for Keycloak and
 * read when the browser comes back, and those two moments are not reliably
 * the same browsing context — a restored tab, a link opened fresh, a return
 * that lands somewhere other than where it started. When the verifier is
 * missing, handleCallback refuses the code before it ever contacts Keycloak,
 * so the user is returned to the login page having just authenticated
 * successfully. Click, authenticate, land on login, repeat: a loop with no
 * error and no server-side trace, because no request was made.
 *
 * It lives in localStorage instead, which is what every OIDC client library
 * does with it, and it costs nothing: the verifier is single-use and useless
 * without the matching authorization code, and it is deleted the moment it is
 * consumed — including when the exchange fails.
 */

import type { AuthProvider } from "ra-core";

import { getCrmConfig } from "@/lib/crmConfig";
import { clearToken, getToken } from "./token";

const KEY_ACCESS = "crm.kc.access";
const KEY_REFRESH = "crm.kc.refresh";
// Deliberately localStorage — see the note above. Both are consumed and
// removed by the first callback that runs.
const KEY_VERIFIER = "crm.kc.verifier";
const KEY_RETURN = "crm.kc.return";

const b64url = (bytes: ArrayBuffer) =>
  btoa(String.fromCharCode(...new Uint8Array(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

const makeVerifier = () => b64url(crypto.getRandomValues(new Uint8Array(32)).buffer);

const challenge = async (verifier: string) =>
  b64url(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));

const oidc = async () => {
  const cfg = await getCrmConfig();
  const base = `${cfg.keycloakUrl}/realms/${cfg.keycloakRealm}/protocol/openid-connect`;
  return { cfg, authorize: `${base}/auth`, token: `${base}/token`, logout: `${base}/logout` };
};

const decodeExp = (jwt: string): number => {
  try {
    const json = JSON.parse(
      atob(jwt.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    return typeof json.exp === "number" ? json.exp * 1000 : 0;
  } catch {
    return 0;
  }
};

/** A valid Keycloak access token, refreshing if it is close to expiry. */
const accessToken = async (): Promise<string> => {
  const tok = sessionStorage.getItem(KEY_ACCESS);
  if (tok && Date.now() < decodeExp(tok) - 30_000) return tok;

  const refresh = sessionStorage.getItem(KEY_REFRESH);
  if (!refresh) throw new Error("not authenticated");

  const { cfg, token } = await oidc();
  const res = await fetch(token, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: cfg.clientId,
      refresh_token: refresh,
    }),
  });
  if (!res.ok) {
    // The refresh token is spent or the session was ended elsewhere. Clearing
    // here means the next checkAuth sends the user to login rather than
    // looping on a dead token.
    sessionStorage.removeItem(KEY_ACCESS);
    sessionStorage.removeItem(KEY_REFRESH);
    throw new Error("session expired");
  }
  const body = await res.json();
  sessionStorage.setItem(KEY_ACCESS, body.access_token);
  if (body.refresh_token) sessionStorage.setItem(KEY_REFRESH, body.refresh_token);
  return body.access_token;
};

export const getKeycloakToken = accessToken;

/**
 * Synchronous "is there a session in this tab": a stored access or refresh
 * token. Deliberately NOT `accessToken()` — that is async, and a Promise is
 * truthy even when it is about to reject, which is how the login page's
 * `?sso=1` shortcut once sent every fresh visitor to `#/` instead of Keycloak.
 * Whether the token is still valid is checkAuth's job, not this one's.
 */
export const hasStoredSession = (): boolean => {
  try {
    return !!(sessionStorage.getItem(KEY_ACCESS) || sessionStorage.getItem(KEY_REFRESH));
  } catch {
    return false;
  }
};

/**
 * Where Keycloak sends the browser back, and it is the APP ROOT.
 *
 * It used to be `/auth-callback.html`, a second document that the service
 * worker answered with index.html on every single navigation -- so the page
 * never ran and no login ever completed. See the long note in App.tsx. Keeping
 * one entry document is what makes that impossible rather than merely fixed.
 *
 * Keycloak compares this string EXACTLY, and it appears in both the
 * authorization request and the token exchange. The two must be byte-identical
 * or the exchange fails with `invalid_grant`, so they read it from here.
 */
const redirectUri = () => `${window.location.origin}/`;

/**
 * Where to land after a sign-in, given where the user set off from.
 *
 * The entry routes are not destinations: `#/login` is the page they were on
 * because they were not signed in, and `#/auth-callback` is a mechanism.
 * Returning to either one undoes the sign-in that just succeeded.
 *
 * Applied on the WAY OUT and again on the way BACK. The stored value outlives
 * the deploy that starts writing a better one — anyone mid-loop already has
 * `#/login` sitting in localStorage, and a fix that only guards the write
 * would leave them exactly where they are.
 */
const ENTRY_ROUTES = /^#\/(login|auth-callback)\b/;

const returnTarget = (hash: string | null | undefined): string =>
  !hash || ENTRY_ROUTES.test(hash) ? "#/" : hash;

let callbackInFlight: Promise<void> | null = null;

const exchangeCode = async (): Promise<void> => {
  const url = new URL(window.location.href);

  // The error branch first. A user who declined consent, or a client
  // misconfiguration, arrives with ?error= and no code -- and used to get a
  // login screen with no explanation, which invites trying the same thing
  // again.
  const oauthError = url.searchParams.get("error");
  if (oauthError) {
    throw new Error(url.searchParams.get("error_description") || oauthError);
  }

  const code = url.searchParams.get("code");
  const verifier = localStorage.getItem(KEY_VERIFIER);
  if (!code || !verifier) {
    // Say which half is missing. "invalid authentication callback" covering
    // both is what made this look like Keycloak rejecting the login, when in
    // fact nothing had been sent to it.
    throw new Error(
      code
        ? "this sign-in was started somewhere else; please try again"
        : "no authorization code in the callback",
    );
  }

  const { cfg, token } = await oidc();
  const res = await fetch(token, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: cfg.clientId,
      code,
      redirect_uri: redirectUri(),
      code_verifier: verifier,
    }),
  });
  localStorage.removeItem(KEY_VERIFIER);
  if (!res.ok) throw new Error(`token exchange failed: HTTP ${res.status}`);

  const body = await res.json();
  sessionStorage.setItem(KEY_ACCESS, body.access_token);
  if (body.refresh_token) sessionStorage.setItem(KEY_REFRESH, body.refresh_token);

  // Mint the PostgREST token now rather than on the first query. If this
  // organization has no CRM, the user finds out here, with a message,
  // instead of via an empty list that looks like their data vanished.
  await getToken(accessToken);

  const back = returnTarget(localStorage.getItem(KEY_RETURN));
  localStorage.removeItem(KEY_RETURN);
  // Drops the spent ?code= from the address bar along the way.
  window.location.replace(`/${back}`);
  return new Promise<void>(() => {});
};

export const getAuthProvider = (): AuthProvider => ({
  async login() {
    // Say WHY when this cannot start. A rejected login shows the message
    // react-admin was given; an opaque one reads as "the button is broken",
    // which is indistinguishable from nothing having happened at all.
    let cfg, authorize;
    try {
      ({ cfg, authorize } = await oidc());
    } catch (e) {
      throw new Error(
        "could not load the CRM configuration — check your connection and try again",
      );
    }
    const verifier = makeVerifier();
    localStorage.setItem(KEY_VERIFIER, verifier);
    // Come back to where the user actually was, not the root — a deep link
    // that bounces you to the dashboard after login is a small betrayal that
    // people notice every time.
    //
    // EXCEPT WHEN WHERE THEY WERE IS THE LOGIN PAGE, which is where almost
    // everyone is when they click Sign in. Storing `#/login` here sent the
    // user back to the login screen after a COMPLETELY SUCCESSFUL sign-in:
    // Keycloak authenticated them, the code was exchanged, the CRM token was
    // minted — and then the app navigated to the one route that offers to
    // sign them in again. An infinite loop made entirely of successes, which
    // is why it left no error anywhere.
    localStorage.setItem(KEY_RETURN, returnTarget(window.location.hash));

    const params = new URLSearchParams({
      client_id: cfg.clientId,
      redirect_uri: redirectUri(),
      response_type: "code",
      scope: "openid email profile",
      code_challenge: await challenge(verifier),
      code_challenge_method: "S256",
    });
    window.location.assign(`${authorize}?${params}`);
    // Never resolves: the browser is navigating away.
    return new Promise<void>(() => {});
  },

  async handleCallback() {
    // SINGLE-FLIGHT, BECAUSE <StrictMode> DOUBLE-INVOKES EFFECTS.
    // react-admin calls this from an effect; in development, and after any
    // future remount, a second call would try to redeem a code Keycloak has
    // already spent and get HTTP 400. That reads as an intermittent login
    // failure, which is the worst kind to chase. Same pattern as token.ts.
    if (!callbackInFlight) callbackInFlight = exchangeCode();
    return callbackInFlight;
  },

  async logout() {
    const refresh = sessionStorage.getItem(KEY_REFRESH);
    sessionStorage.removeItem(KEY_ACCESS);
    sessionStorage.removeItem(KEY_REFRESH);
    clearToken();
    // End the Keycloak session too. Clearing only local state leaves the realm
    // cookie intact, so "log out" followed by "log in" silently returns the
    // same user — which on a shared machine is a real problem.
    try {
      const { cfg, logout } = await oidc();
      if (refresh) {
        await fetch(logout, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: new URLSearchParams({ client_id: cfg.clientId, refresh_token: refresh }),
          keepalive: true,
        });
      }
    } catch {
      /* best effort — local state is already gone */
    }
  },

  async checkAuth() {
    await accessToken();
  },

  async checkError(error: any) {
    if (error?.status === 401) {
      clearToken();
      throw new Error("session expired");
    }
    // A 403 is authorization, not authentication: re-logging in would land the
    // user in the same place. Let the UI show it.
  },

  /**
   * THE ID HERE IS THE `sales` ROW, NOT THE KEYCLOAK SUBJECT.
   *
   * atomic-crm treats `getIdentity().id` as a foreign key: CompanyCreate,
   * ContactCreate, NoteCreate and the "only mine" filters all put it straight
   * into `sales_id`, a BIGINT. Returning `claims.sub` sent a UUID there and
   * every create died with
   *
   *     invalid input syntax for type bigint: "c4b10802-…"
   *
   * while the app itself looked perfectly healthy. The name and email still
   * come from the token — those ARE the person; only the id is the CRM's.
   */
  async getIdentity() {
    const tok = await accessToken();
    const claims = JSON.parse(
      atob(tok.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")),
    );
    const minted = await getToken(accessToken);
    return {
      id: minted.salesId,
      fullName: claims.name ?? claims.preferred_username ?? claims.email,
      email: claims.email,
    };
  },

  async getPermissions() {
    return [];
  },
});
